import type { MaybeRefOrGetter } from 'vue';
import { USER_ROLES } from '~/constants/auth';
import { developerPoolPercent } from '~/utils/properties/commissionPoolPercent';

export function usePropertyCommissionEstimate(
  property: MaybeRefOrGetter<unknown>,
  unitPriceSource?: MaybeRefOrGetter<number | null | undefined>,
) {
  const authStore = useAuthStore();
  const { selfClosePercent: standardSelfClosePercent, isUaeShare, isLoaded: isSplitLoaded } = useAgentStandardSplit();

  const resolved = computed(() => toValue(property));
  const unitPrice = computed(() => {
    const v = unitPriceSource == null ? null : toValue(unitPriceSource);
    return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
  });

  const canApplyAgentTierSplit = computed(() => {
    const role = authStore.user?.role;
    // Masterclass leads (LEAD) get full agent-equivalent platform access, so
    // they see the same tier-split commission estimate as agents.
    return (
      role === USER_ROLES.AGENT ||
      role === USER_ROLES.ADMIN ||
      role === USER_ROLES.ACCOUNT_MANAGER ||
      role === USER_ROLES.LEAD
    );
  });

  const poolPercent = computed(() => developerPoolPercent(resolved.value));
  // Agent figures wait for /me so no tier-row % shows first.
  const isSplitPending = computed(() => canApplyAgentTierSplit.value && !isSplitLoaded.value);

  const tierSplitPercent = computed(() => {
    if (!canApplyAgentTierSplit.value) return null as number | null;
    return standardSelfClosePercent.value;
  });

  const effectivePercentOfPrice = computed(() => {
    const pool = poolPercent.value;
    const split = tierSplitPercent.value;
    if (split == null) return pool;
    return Math.round(((pool * split) / 100) * 100) / 100;
  });

  const estimatedAmountFromMinPrice = computed(() => {
    const p = resolved.value as { price?: { min?: number } } | null | undefined;
    // Prefer the selected unit's price when provided; fall back to the project min.
    const price = (unitPrice.value ?? Number(p?.price?.min)) || 0;
    const pct = effectivePercentOfPrice.value;
    return Math.round(price * (pct / 100));
  });

  const commissionBadgeTitle = computed(() => {
    if (!canApplyAgentTierSplit.value || tierSplitPercent.value == null) return '';
    const share = isUaeShare.value ? 'your share' : 'your tier';
    return `Project offer ${poolPercent.value}% × ${share} ${tierSplitPercent.value}%`;
  });

  return {
    isAgent: canApplyAgentTierSplit,
    isUaeShare,
    isSplitPending,
    poolPercent,
    tierSplitPercent,
    effectivePercentOfPrice,
    estimatedAmountFromMinPrice,
    commissionBadgeTitle,
  };
}
